import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext, Store } from '@ngxs/store'
import { tap } from 'rxjs/operators'
import { ShopsState } from '@lla-platform/shops/shops-data-access'
import { AttendanceStateModel } from './attendance.model'
import { AttendanceService } from '../attendance.service'
import { EmployeeAttendanceDetails, GetAttendance, ResyncAttendance } from './attendance.actions'
import { IAttendanceResponse } from '../../interfaces/attendance.interface'
import { AttendanceCardRules, isAlertDay } from '../../utils/attendance-card-filters.util'

@State<AttendanceStateModel>({
  name: 'attendance'
})
@Injectable()
export class AttendanceState {
  constructor(
    private attendanceService: AttendanceService,
    private store: Store
  ) {}

  @Selector()
  static availableWeeks(state: AttendanceStateModel) {
    return state.availableWeeks ?? []
  }

  // undefined until the week has loaded, so subscribers can tell "loading" from "no locations"
  @Selector()
  static response(state: AttendanceStateModel): IAttendanceResponse | undefined {
    if (!state.response) {
      return undefined
    }
    return {
      ...state.response,
      locations: (state.response.locations ?? []).map((el) => ({
        ...el,
        peopleOnSite: el.employees?.filter(AttendanceCardRules.onSite)?.length ?? 0,
        // counted in days, not employees, so it does not come from the card rule
        alerts: el.employees?.map((el) => el.days?.filter(isAlertDay))?.flat()?.length ?? 0,
        away: el.employees?.filter(AttendanceCardRules.away)?.length ?? 0,
        underperforming: el.employees?.filter(AttendanceCardRules.underperforming)?.length ?? 0
      }))
    }
  }

  @Selector()
  static responsePeriod(state: AttendanceStateModel) {
    return state.response?.currentWeek
  }

  @Selector()
  static employeeDetails(state: AttendanceStateModel) {
    return state.employeeDetails
  }

  @Action(GetAttendance)
  getAttendance(ctx: StateContext<AttendanceStateModel>, { weekNumber }: GetAttendance) {
    ctx.patchState({
      response: undefined
    })
    return this.attendanceService
      .getAttendance(weekNumber)
      .pipe(tap((res) => ctx.patchState({ response: this.toResponse(res) })))
  }

  @Action(ResyncAttendance)
  resyncAttendance(
    ctx: StateContext<AttendanceStateModel>,
    { weekNumber, locationIds }: ResyncAttendance
  ) {
    ctx.patchState({
      response: undefined
    })
    return this.attendanceService
      .resyncAttendance(weekNumber, locationIds)
      .pipe(tap((res) => ctx.patchState({ response: this.toResponse(res) })))
  }

  private toResponse(res: IAttendanceResponse): IAttendanceResponse {
    const allShops = this.store.selectSnapshot(ShopsState.allShops)
    return {
      ...res,
      locations: (res?.locations ?? []).map((el) => ({
        ...el,
        locationInfo: allShops.find((s) => s.id === el.location?.id)
      }))
    }
  }

  @Action(EmployeeAttendanceDetails)
  employeeAttendanceDetails(
    ctx: StateContext<AttendanceStateModel>,
    { employeeId, from }: EmployeeAttendanceDetails
  ) {
    ctx.patchState({
      employeeDetails: undefined
    })
    return this.attendanceService.employeeAttendanceDetails(employeeId, from).pipe(
      tap((res) => {
        ctx.patchState({
          employeeDetails: res
        })
      })
    )
  }
}
